import React, { useMemo, useRef, useState, useEffect } from 'react';
import TableLayout from '../../molecules/TableLayout';
import Table from '../../molecules/Table';
import Field from '../Field';
import Form from '../../molecules/Form/Form';
import { useForm } from '../../molecules/Form';
import { useContextHook } from 'use-context-hook';
import { SubListLeadsContext } from '../../../Context/subListLeadsContext.jsx';
import { MdKeyboardArrowDown, MdKeyboardArrowLeft, MdSort } from 'react-icons/md';
import Button from '../../molecules/Button';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledLeadsTasks, StyledSingleLeads } from './LeadsDetails.styles';
import { format } from 'date-fns';
import loadingIcon from '../../../assets/icon-loading.gif';
import dummyImg from '../../../assets/avatar-2.svg';
import { capitalize, formatDate, formatStatus, FormatText, getAvatarSrc, handleAvatarError, handleDebouncedSearch, handleSearchNow } from '../../../helpers/common.js';
import { AuthContext } from '../../../Context/authContext.jsx';
import { IoMdSettings } from 'react-icons/io';
import { IoAdd, IoTrashOutline, IoCheckboxOutline, IoCloseOutline } from 'react-icons/io5';
import { IoIosArrowDown } from 'react-icons/io';
import Toast from '../../molecules/Toast/index.jsx';
import linkedinService from '../../../services/linkedinService.js';
import usePersistedSearchQuery from '../../../hooks/usePersistedSearchQuery';
import iconMenu from '../../../assets/icon-menu-dots-vertical.svg';
import CenterModal from '../../molecules/Modal/CenterModal';
import campaignService from '../../../services/campaignService';
import AddToModalContent from './AddToModalContent';
import ResyncModalContent from './ResyncModalContent';
import CreateListModal from '../CreateListModal';
import { FaFilter } from 'react-icons/fa6';
import Filters from './Filters.jsx';
import Loaders from '../Loaders';

const SingleListLeadsDetails = () => {
  const navigate = useNavigate();
  const [form] = useForm();
  const { id } = useParams();
  const [quickActive, setQuickActive] = useState(false);
  const [tags, setTags] = useState([]);
  const quickRef = useRef(null);
  const filtersRef = useRef(null);
  const filtersSidebarRef = useRef(null);
  const debounceRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const [addToCampaignModal, setAddToCampaignModal] = useState({ open: false, leadId: null });
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [addToCampaignLoading, setAddToCampaignLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [openUpdateFiltersModal, setOpenUpdateFiltersModal] = useState(false);
  const [sublistDetails, setSublistDetails] = useState(null);
  const [resyncModal, setResyncModal] = useState({ open: false, leadsAdded: 0 });
  const [resyncLoading, setResyncLoading] = useState(false);
  const DropdownRef = useRef(null);
  const bulkDropdownRef = useRef(null);
  const [failedAvatarSources, setFailedAvatarSources] = useState(() => new Set());

  // Selection state
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [totalSelectedCount, setTotalSelectedCount] = useState(0);
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);
  const [showBulkDropdown, setShowBulkDropdown] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);


  const { subListName } = useContextHook(SubListLeadsContext, v => ({
    subListName: v.subListName,
  }));

  const [searchQuery, setSearchQuery] = usePersistedSearchQuery({
    id: id, // dynamic
    page: 1,
    itemsPerPage: 10,
    searchText: '',
    getAll: false,
    status: [],
    contactedStatus: 'ALL',
    source: [],
    campaigns: [],
    tagsFilter: [],
    listsFilter: [],
    dateCreatedFilter: '',
    jobTitle: '',
    companyName: '',
    companyNames: [],
    language: '',
    languages: [],
    locations: [],
    location: '',
    industry: '',
    industries: [],
    headline: '',
    headlines: [],
    emailExists: 'ALL',
    linkedinProfileExists: 'ALL',
    openToWork: 'ALL',
    isHiring: 'ALL',
  });

  // Use context for sublist leads data
  const { leadsData, leadsLoading, updateSearchQuery, filters, inCampaign } = useContextHook(SubListLeadsContext, v => ({
    leadsData: v.leadsData,
    leadsLoading: v.leadsLoading,
    updateSearchQuery: v.updateSearchQuery,
    filters: v.filters,
    inCampaign: v.inCampaign,
  }));

  const { refetch } = useContextHook(AuthContext, v => ({
    refetch: v.refetch,
  }));

  useEffect(() => {
    if (searchQuery?.id) {
      updateSearchQuery(searchQuery);
    }
  }, [JSON.stringify(searchQuery), updateSearchQuery]);

  const leads_data = leadsData;
  const leads_loading = leadsLoading;


  const hasFilters = useMemo(() => {
    if (!filters || typeof filters !== 'object') return false;
    if (Object.keys(filters || {}).length === 0) return false;
    if (Array.isArray(filters.conditions) && filters.conditions.length === 0) return false;
    return true;
  }, [filters]);

  // Selection handlers
  const handleSelectAll = async () => {
    const totalItems = leads_data?.data?.totalItems || 0;

    if (selectedLeads.length === totalItems && totalItems > 0) {
      setSelectedLeads([]);
      setTotalSelectedCount(0);
      setIsSelectAllMode(false);
      return;
    }

    if (selectAllLoading) return;

    setSelectAllLoading(true);
    try {
      const allLeadIds = await linkedinService.getAllSublistLeads({
        ...searchQuery,
        getAll: true,
      });

      const leadIds = allLeadIds?.data?.ids || [];
      setSelectedLeads(leadIds);
      setTotalSelectedCount(leadIds.length);
      setIsSelectAllMode(true);
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to select all leads' });
      setSelectedLeads([]);
      setTotalSelectedCount(0);
      setIsSelectAllMode(false);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleSelectLead = (leadId, checked) => {
    setTotalSelectedCount(0);
    setIsSelectAllMode(false);
    if (checked) {
      setSelectedLeads(prev => [...prev, leadId]);
    } else {
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    }
  };

  const isAllSelected = useMemo(() => {
    const currentVisibleIds = leads_data?.data?.items?.map(item => item.id) || [];
    if (currentVisibleIds.length === 0) return false;

    const totalItems = leads_data?.data?.totalItems || 0;
    if (isSelectAllMode) return true;
    const allVisibleSelected = currentVisibleIds.every(id => selectedLeads.includes(id));
    const allItemsSelected = selectedLeads.length === totalItems && totalItems > 0;

    return allVisibleSelected && allItemsSelected;
  }, [selectedLeads, leads_data, isSelectAllMode]);

  const isIndeterminate = useMemo(() => {
    return selectedLeads.length > 0 && !isAllSelected;
  }, [selectedLeads, isAllSelected]);

  const visibleSelectedCount = useMemo(() => {
    if (isSelectAllMode) {
      return totalSelectedCount;
    }
    return selectedLeads.length;
  }, [selectedLeads, totalSelectedCount]);

  const handleUnselectAll = () => {
    setSelectedLeads([]);
    setTotalSelectedCount(0);
    setIsSelectAllMode(false);
    setShowBulkDropdown(false);
  };

  const handleSelectAllLeads = async () => {
    if (selectAllLoading) return;

    setSelectAllLoading(true);
    setShowBulkDropdown(false);

    try {
      const allLeadIds = await linkedinService.getAllSublistLeads({
        ...searchQuery,
        getAll: true,
      });

      const leadIds = allLeadIds?.data?.ids || [];
      setSelectedLeads(leadIds);
      setTotalSelectedCount(leadIds.length);
      setIsSelectAllMode(true);
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to select all leads' });
      setSelectedLeads([]);
      setTotalSelectedCount(0);
      setIsSelectAllMode(false);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleBulkAddToCampaign = () => {
    setShowBulkDropdown(false);
    setAddToCampaignModal({ open: true, leadId: selectedLeads });
    setSelectedCampaign(null);
    setPreviewData(null);
    fetchCampaigns();
  };

  const handleBulkRemoveFromList = async () => {
    if (selectedLeads.length === 0) {
      Toast({ type: 'error', message: 'No leads selected' });
      return;
    }

    setShowBulkDropdown(false);
    setRemoveLoading(true);

    try {
      const payload = {
        listId: Number(id),
        leadId: selectedLeads,
      };

      const response = await linkedinService.removeLeadFromSublist(payload);

      Toast({
        type: 'success',
        message: `${selectedLeads.length} lead${selectedLeads.length > 1 ? 's' : ''} removed from list successfully`
      });

      setSelectedLeads([]);
      setTotalSelectedCount(0);
      setIsSelectAllMode(false);
      setSearchQuery(prev => ({ ...prev, _refresh: Date.now() }));
      refetch();
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to remove leads from list' });
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleSort = field => {
    setSearchQuery(prev => {
      const newDirection = prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc';
      return { ...prev, sortField: field, sortDirection: newDirection, page: 1 };
    });
  };

  const renderSortableHeader = (label, field) => {
    const isActive = searchQuery.sortField === field;
    const direction = searchQuery.sortDirection;

    return (
      <div
        className="sortable-header"
      // onClick={() => handleSort(field)}
      >
        {label}
        {/* {!isActive && <MdSort className="icon" />}
        {isActive && direction === 'asc' && <MdSort className="icon" />}
        {isActive && direction === 'desc' && <MdSort className="icon" />} */}
      </div>
    );
  };

  const columnNames = [
    <div className="th-label-box">
      <div className="checkbox-wrapper">
        <Field
          type="checkbox"
          name="select_all"
          noMargin
          label={<></>}
          value={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={handleSelectAll}
        />
        <div className="bulk-dropdown-wrapper" ref={bulkDropdownRef}>
          <button
            type="button"
            className="bulk-dropdown-trigger"
            onClick={(e) => {
              e.stopPropagation();
              setShowBulkDropdown(prev => !prev);
            }}>
            {visibleSelectedCount > 0
              ? `${visibleSelectedCount} Selected`
              : ''}
            <IoIosArrowDown size={16} />
          </button>
          {showBulkDropdown && (
            <div className="bulk-dropdown-menu">
              <ul className="bulk-dropdown-list">
                <li>
                  <button
                    type="button"
                    className="bulk-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAllLeads();
                    }}
                    disabled={selectAllLoading}
                    style={{ opacity: selectAllLoading ? 0.6 : 1, cursor: selectAllLoading ? 'not-allowed' : 'pointer' }}>
                    <IoCheckboxOutline size={18} />
                    {selectAllLoading ? 'Selecting...' : 'Select All'}
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="bulk-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnselectAll();
                    }}>
                    <IoCloseOutline size={18} />
                    Unselect All
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="bulk-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBulkAddToCampaign();
                    }}
                    disabled={visibleSelectedCount === 0}
                    style={{ opacity: visibleSelectedCount === 0 ? 0.5 : 1, cursor: visibleSelectedCount === 0 ? 'not-allowed' : 'pointer' }}>
                    <IoAdd size={18} />
                    Add to Campaign
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="bulk-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBulkRemoveFromList();
                    }}
                    disabled={visibleSelectedCount === 0 || removeLoading}
                    style={{ opacity: (visibleSelectedCount === 0 || removeLoading) ? 0.5 : 1, cursor: (visibleSelectedCount === 0 || removeLoading) ? 'not-allowed' : 'pointer' }}>
                    <IoTrashOutline size={18} />
                    {removeLoading ? 'Removing...' : 'Remove from List'}
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      <span className="column-label">Name</span>
    </div>,
    renderSortableHeader('Status', 'status'),
    renderSortableHeader('Created date', 'created_date'),
    'Company',
    'Position',
    'Email',
    'Action',
  ];

  const Status = data => {
    switch (data.status) {
      case 'UPLOADING':
        return (
          <div className="badge uploading">
            <img src={loadingIcon} alt="loading" width="15" height="15" />
            &nbsp;
            {formatStatus(data?.status)}
          </div>
        );
      case 'NEW':
        return <div className="badge new">{formatStatus(data?.status)}</div>;
      case 'ENRICHING':
        return (
          <div className="badge uploading">
            <img src={loadingIcon} alt="loading" width="15" height="15" />
            &nbsp;
            {formatStatus(data?.status)}
          </div>
        );
      case 'AWAITING_LINKEDIN':
        return (
          <div
            className="badge new"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={e => {
              e.stopPropagation();
              navigate('/linkedin-accounts');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                navigate('/linkedin-accounts');
              }
            }}>
            Connect LinkedIn to enrich
          </div>
        );
      case 'ENRICHING_EMAIL':
        return (
          <div className="badge uploading">
            {' '}
            <img src={loadingIcon} alt="loading" width="15" height="15" />
            &nbsp;
            {formatStatus(data?.status)}
          </div>
        );
      case 'EMAIL_ENRICHMENT_DONE':
        return <div className="badge done">{formatStatus(data?.status)}</div>;
      case 'ENRICHING_MESSAGE':
        return (
          <div className="badge uploading">
            <img src={loadingIcon} alt="loading" width="15" height="15" />
            &nbsp;
            {formatStatus(data?.status)}
          </div>
        );
      case 'MESSAGE_ENRICHMENT_DONE':
        return <div className="badge done">{formatStatus(data?.status)}</div>;
      case 'IN_CAMPAIGN':
        return (
          <div className="badge uploading">
            {' '}
            <img src={loadingIcon} alt="loading" width="15" height="15" />
            &nbsp;
            {formatStatus(data?.status)}
          </div>
        );
      case 'CAMPAIGN_DONE':
        return <div className="badge done">{formatStatus(data?.status)}</div>;
      case 'REPLIED':
        return <div className="badge replied">{formatStatus(data?.status)}</div>;
      case 'UNSUBSCRIBED':
        return <div className="badge unsubscribed">{formatStatus(data?.status)}</div>;
      case 'BLOCKED':
        return <div className="badge blocked">{formatStatus(data?.status)}</div>;
      case 'BOUNCED':
        return <div className="badge bounced">{formatStatus(data?.status)}</div>;
      case 'OPENED_EMAIL':
        return <div className="badge opend">{formatStatus(data?.status)}</div>;
      case 'CLICKED_LINK':
        return <div className="badge clicked">{formatStatus(data?.status)}</div>;
      default:
        break;
    }
  };

  // While a lead is being enriched its company/title/email arrive asynchronously,
  // so show a subtle hint instead of a bare '-' for those still-empty cells.
  const enrichingHint = data =>
    data?.status === 'ENRICHING' ? (
      <span className="enriching-hint" style={{ color: '#9CA3AF', display: 'inline-flex', alignItems: 'center' }}>
        <img src={loadingIcon} alt="enriching" width="12" height="12" />
        &nbsp;Enriching…
      </span>
    ) : (
      '-'
    );

  const toggleDropdown = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDropdown(prevId => (prevId === id ? null : id));
  };

  const ActionBtn = data => (
    <ul className="action-list">
      <li className={`action-item ${showDropdown === data?.id ? 'active' : ''}`}>
        <button className="btn" type="button" onClick={e => toggleDropdown(e, data?.id)}>
          <img className="menu" src={iconMenu} width="6" alt="menu" />
        </button>

        {showDropdown === data?.id && (
          <div className="dropdown-menu" ref={DropdownRef}>
            <ul className="dropdown-list">
              <li className="dropdown-item">
                <button
                  type="button"
                  className="item-btn"
                  onClick={e => {
                    e.stopPropagation();
                    handleAddToCampaign(data?.id);
                  }}>
                  <IoAdd size={18} />
                  Add to campaign
                </button>
              </li>
              <li className="dropdown-item">
                <button
                  type="button"
                  className="item-btn"
                  onClick={e => {
                    e.stopPropagation();
                    handleRemoveFromList(data?.id);
                  }}>
                  <IoTrashOutline size={18} />
                  Remove from list
                </button>
              </li>
            </ul>
          </div>
        )}
      </li>
    </ul>
  );

  // Table rows (clickable)
  const { product_rows, totalCount } = useMemo(() => {
    return {
      product_rows: leads_data?.data?.items?.map(data => ({
        id: data?.id,
        style: { cursor: 'pointer' },
        isDropdownOpen: showDropdown === data?.id,
        cells: [
          // Stop row click when interacting with the checkbox/label
          <div className="lead-td-col" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <Field
              type="checkbox"
              noMargin
              name={data?.id}
              value={selectedLeads.includes(data?.id)}
              onChange={({ target: { value } }) => handleSelectLead(data?.id, value)}
              label={
                <div className="flex-content" onClick={() => navigate(`/lead-preview/${data?.id}`)}>
                  <div className="avatar-wrap">
                    <img
                      src={getAvatarSrc(data?.profile_photo, failedAvatarSources, dummyImg)}
                      alt={data.name || 'Avatar'}
                      className="avatar"
                      onError={() => handleAvatarError(data?.profile_photo, setFailedAvatarSources)}

                    />
                  </div>
                  <div className="text-wrap">
                    <span className="name">
                      {`${data?.first_name || ''} ${capitalize(data?.last_name) || ''}` || '-'}
                    </span>
                  </div>
                </div>
              }
            />
          </div>,
          <div className="badge-col" style={{ display: 'flex' }}>
            {Status(data) || '-'}
          </div>,
          formatDate(data?.created_at),
          data?.company_name || enrichingHint(data),
          <span className="tag-text" style={{ color: '#0DA06A' }}>
            {data?.title || enrichingHint(data)}
          </span>,
          data?.email || enrichingHint(data),
          ActionBtn({ id: data?.id }),
        ],
      })),
      totalCount: leads_data?.data?.totalItems,
    };
  }, [leads_data, showDropdown, selectedLeads, failedAvatarSources]);

  const handleQuickAction = async () => {
    try {
      const payload = {
        list_id: Number(id),
      };
      const res = await linkedinService.emailEnrichment(payload);

      if (res.success) {
        Toast({ type: 'success', message: 'Email Enrichment Initiated' });
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message });
    }
  };

  const handleUpdateFilters = async () => {
    try {
      // Use filters from context instead of making separate API call
      const listData = {
        id: id,
        name: subListName,
        filters: filters || { conditions: [] },
      };

      setSublistDetails(listData);
      setOpenUpdateFiltersModal(true);
      setQuickActive(false);
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to open update filters' });
    }
  };

  const handleResync = async () => {
    try {
      setQuickActive(false);

      // If list is in campaign, show confirmation modal first
      if (inCampaign) {
        setResyncModal({ open: true, leadsAdded: 0 });
        return;
      }

      // If not in campaign, proceed with resync directly
      setResyncLoading(true);
      await performResync(false);
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to resync list' });
    } finally {
      setResyncLoading(false);
    }
  };


  const performResync = async (addToCampaign) => {
    try {
      const res = await linkedinService.resyncSublist({ id, addToCampaign });

      if (!res.success) return;

      const { alreadyInSync, leadsAdded } = res.data || {};

      // Handle already in sync case
      if (alreadyInSync) {
        Toast({ type: 'info', message: 'This list is already up to date.' });
        return;
      }


      const getSuccessMessage = () => {
        const leadCount = leadsAdded || 0;
        const leadText = leadCount === 1 ? 'lead' : 'leads';

        if (addToCampaign) {
          return leadCount > 0
            ? `${leadCount} new ${leadText} added to campaign successfully`
            : 'List resynced and added to campaign successfully';
        }

        return leadCount > 0
          ? `List resynced successfully. ${leadCount} new ${leadText} added.`
          : 'List resync initiated successfully';
      };

      Toast({ type: 'success', message: getSuccessMessage() });
      handleRefreshSublistLeads();
      refetch();
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to resync list' });
    } finally {
      setResyncLoading(false);
    }
  };

  // Yes button - Add resynced leads to campaign
  const handleAddResyncLeadsToCampaign = async () => {
    setResyncLoading(true);
    setResyncModal({ open: false, leadsAdded: 0 });
    await performResync(true);
  };

  // No button - Resync without adding to campaign
  const handleCancelResync = async () => {
    setResyncLoading(true);
    setResyncModal({ open: false, leadsAdded: 0 });
    await performResync(false);
  };

  // Close modal without API call (for X button)
  const handleCloseResyncModal = () => {
    setResyncModal({ open: false, leadsAdded: 0 });
  };

  const handleRefreshSublistLeads = () => {
    // Force refresh by updating searchQuery state with a timestamp to trigger re-fetch

    setSearchQuery(prev => ({ ...prev, _refresh: Date.now(), page: 1 }));
  };

  const handleRemoveFromList = async (leadId) => {
    if (!leadId) return;

    setShowDropdown(null);
    setRemoveLoading(true);
    try {
      const response = await linkedinService.removeLeadFromSublist({
        listId: id,
        leadId: leadId,
      });

      Toast({ type: 'success', message: 'Lead removed from list successfully' });
      refetch();
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to remove lead from list' });
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleAddToCampaign = (leadId) => {
    setShowDropdown(null);
    setAddToCampaignModal({ open: true, leadId });
    setSelectedCampaign(null);
    fetchCampaigns();
  };

  const fetchCampaigns = async () => {
    try {
      const res = await linkedinService.getAllCampaigns();
      if (res?.success && res?.data) {
        const formattedCampaigns = res.data.map(campaign => ({
          label: campaign?.name,
          value: campaign?.id?.toString(),
          campaign_id: campaign?.id,
          status: campaign?.status,
        }));

        // const filteredCampaigns = formattedCampaigns.filter(
        //   campaign => campaign.status === 'draft' || campaign.status === 'running',
        // );

        setCampaigns(formattedCampaigns);
      }
    } catch (error) {
      Toast({ type: 'error', message: 'Failed to fetch campaigns' });
    }
  };

  const handleCampaignSelection = async campaignId => {
    setSelectedCampaign(campaignId);
    setPreviewData(null);

    if (!campaignId) return;

    const selectedCampaignData = campaigns.find(c => c.value === campaignId);
    if (!selectedCampaignData) return;

    // Show preview only for bulk operations
    if (Array.isArray(addToCampaignModal.leadId) && addToCampaignModal.leadId.length > 1) {
      setPreviewLoading(true);
      try {
        const payload = {
          leadId: addToCampaignModal.leadId,
          campaignId: selectedCampaignData.campaign_id,
        };

        const response = await campaignService.getUserLeadsPreview(payload);
        setPreviewData(response?.data || null);
      } catch (error) {
        Toast({ type: 'error', message: error.message || 'Failed to preview leads' });
        setPreviewData(null);
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  const handleAddLeadToCampaign = async () => {
    if (!selectedCampaign) {
      Toast({ type: 'error', message: 'Please select a campaign' });
      return;
    }

    const selectedCampaignData = campaigns.find(c => c.value === selectedCampaign);
    if (!selectedCampaignData) {
      Toast({ type: 'error', message: 'Campaign not found' });
      return;
    }

    setAddToCampaignLoading(true);
    try {
      const isBulk = Array.isArray(addToCampaignModal.leadId);
      const leadIds = isBulk ? addToCampaignModal.leadId : [addToCampaignModal.leadId];

      const payload = {
        leadId: leadIds,
        campaignId: selectedCampaignData.campaign_id,
        name: selectedCampaignData.label,
      };

      const response = await campaignService.addUserLeadToCampaign(payload);

      const addedCount = previewData?.uniqueLeads || leadIds.length;
      const successMessage = isBulk
        ? `${addedCount} ${addedCount === 1 ? 'lead' : 'leads'} added to ${response?.data?.campaignName || selectedCampaignData.label}`
        : `1 Lead added to ${response?.data?.campaignName || selectedCampaignData.label} successfully`;

      Toast({ type: 'success', message: successMessage });
      setAddToCampaignModal({ open: false, leadId: null });
      setSelectedCampaign(null);
      setPreviewData(null);

      if (isBulk) {
        setSelectedLeads([]);
        setTotalSelectedCount(0);
        setIsSelectAllMode(false);
      }

      // Refresh sublist data to update inCampaign status
      handleRefreshSublistLeads();
      refetch();
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to add lead to campaign' });
    } finally {
      setAddToCampaignLoading(false);
    }
  };


  const fetchTags = async () => {
    try {
      const res = await linkedinService.getTags();
      if (res?.success && res?.data) {
        const formattedTags = res.data.map(tag => ({
          label: tag?.name,
          value: tag?.id,
        }));
        setTags(formattedTags);
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to fetch tags' });
    }
  };

  const fetchAllLists = async () => {
    try {
      const res = await linkedinService.getAllLists();
      if (res?.success && res?.data) {
        const formattedLists = res.data.map(list => ({
          label: list?.name,
          value: list?.id,
        }));
        setLists(formattedLists);
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to fetch lists' });
    }
  };

  useEffect(() => {
    fetchTags();
    fetchAllLists();
    fetchCampaigns();
  }, []);


  useEffect(() => {
    if (isSelectAllMode) {
      // If user previously clicked "Select All", re-fetch all IDs with new filters
      (async () => {
        try {
          const allLeadIds = await linkedinService.getAllSublistLeads({
            ...searchQuery,
            getAll: true,
          });

          const leadIds = allLeadIds?.data?.ids || [];
          if (leadIds.length > 0) {
            setSelectedLeads(leadIds);
            setTotalSelectedCount(allLeadIds?.data?.totalItems || leadIds.length);
            setIsSelectAllMode(true);
          } else {
            setSelectedLeads([]);
            setTotalSelectedCount(0);
            setIsSelectAllMode(false);
          }
        } catch (error) {
          console.error('Error re-selecting all leads with new filters:', error);
          setSelectedLeads([]);
          setTotalSelectedCount(0);
          setIsSelectAllMode(false);
        }
      })();
    } else {
      setSelectedLeads([]);
      setTotalSelectedCount(0);
    }
  }, [
    searchQuery.status,
    searchQuery.contactedStatus,
    searchQuery.source,
    searchQuery.campaigns,
    searchQuery.tagsFilter,
    searchQuery.dateCreatedFilter,
    searchQuery.jobTitle,
    searchQuery.companyName,
    searchQuery.searchText,
    searchQuery.language,
    searchQuery.locations,
    searchQuery.location,
    searchQuery.industry,
    searchQuery.headline,
    searchQuery.emailExists,
    searchQuery.linkedinProfileExists,
    searchQuery.openToWork,
    searchQuery.isHiring,
  ]);


  useEffect(() => {
    const handleNotification = event => {
      const data = event.detail;
      if (
        data?.actionType === 'email_verifier_submitted' ||
        data?.actionType === 'email_verifier_running' ||
        data?.actionType === 'email_verifier_failed' ||
        data?.actionType === 'email_verifier_succeeded'
      ) {
        refetch();
      }
    };

    window.addEventListener('email-verifier-notification', handleNotification);
    return () => {
      window.removeEventListener('email-verifier-notification', handleNotification);
    };
  }, []);

  // Enrichment runs server-side after a connect, so while any visible lead is
  // still ENRICHING, poll the existing refetch so freshly enriched fields appear
  // without a manual reload. The interval stops once nothing is enriching.
  const hasEnrichingLeads = useMemo(
    () => (leads_data?.data?.items || []).some(lead => lead?.status === 'ENRICHING'),
    [leads_data],
  );

  useEffect(() => {
    if (!hasEnrichingLeads) return undefined;
    const intervalId = setInterval(() => {
      refetch();
    }, 15000);
    return () => clearInterval(intervalId);
  }, [hasEnrichingLeads, refetch]);

  const actionDropdown = event => {
    const isInsideModal = event.target.closest('[role="dialog"]') || event.target.closest('.modal-container');

    if (!isInsideModal && DropdownRef.current && !DropdownRef.current.contains(event.target)) {
      setShowDropdown(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', actionDropdown);
    return () => {
      document.removeEventListener('mousedown', actionDropdown);
    };
  }, [showDropdown]);

  // Close Quick Actions and Filters on outside click
  useEffect(() => {
    const handleDocumentClick = e => {
      // If the user is clicking on a react-select multi-value remove (the "x" on a chip),
      // never treat this as an outside click that closes the filters sidebar.
      if (e.target && e.target.closest('.react-select__multi-value__remove')) {
        return;
      }

      if (quickRef.current && !quickRef.current.contains(e.target)) {
        setQuickActive(false);
      }

      // Treat any click inside the filters UI (including react-select controls/menus)
      // as "inside" so that the sidebar does not close while interacting with filters.
      const clickedInsideFilters =
        (filtersRef.current && filtersRef.current.contains(e.target)) ||
        (filtersSidebarRef.current && filtersSidebarRef.current.contains(e.target)) ||
        e.target.closest('.filters-holder') ||
        e.target.closest('.react-select__menu') ||
        e.target.closest('.react-select__control');

      if (!clickedInsideFilters && openFilters) {
        setOpenFilters(false);
      }

      if (bulkDropdownRef.current && !bulkDropdownRef.current.contains(e.target)) {
        setShowBulkDropdown(false);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [openFilters]);

  // Listen for edit icon click to close Quick Actions dropdown
  useEffect(() => {
    const handleCloseQuickActions = () => {
      setQuickActive(false);
    };
    const handleCloseFilters = () => {
      setOpenFilters(false);
    };
    window.addEventListener('close-quick-actions', handleCloseQuickActions);
    window.addEventListener('close-filters', handleCloseFilters);
    return () => {
      window.removeEventListener('close-quick-actions', handleCloseQuickActions);
      window.removeEventListener('close-filters', handleCloseFilters);
    };
  }, []);

  return (
    <StyledSingleLeads>
      <Form
        form={form}
        onSubmit={values => handleSearchNow(values.search, setSearchQuery)}>
        <div className="filter-wrap single-lead-filter" ref={filtersRef}>
          <Form.Item
            className="input-search"
            colorBg
            inputSm
            noMargin
            type="search"
            rounded
            name="search"
            placeholder="Search Lead"
            onChange={e => handleDebouncedSearch(e.target.value, form, debounceRef, setSearchQuery)}
          >
            <Field />
          </Form.Item>
          <div className="filter-box">
            <button
              type="button"
              className="btn-filter"
              onClick={() => setOpenFilters(!openFilters)}
            >
              <FaFilter className="icon" size={20} />
            </button>
          </div>
          <Button variant="darkBlue" rounded width="150px">
            Search
          </Button>
          {/* <Button variant="light" rounded width="150px">
          Import Leads
        </Button>
        <Button variant="light" rounded width="150px">
          Add lead
        </Button> */}
          <div className="edit-name-header">
            <div className="quick-action-box" ref={quickRef}>
              <Button
                className={`btn-quick ${quickActive ? 'active' : ''}`}
                newStyle
                rounded
                variant="outline"
                width="168px"
                onClick={e => {
                  e.stopPropagation();
                  setQuickActive(prev => {
                    const newState = !prev;
                    // Dispatch event to close Credits dropdown in Header when opening
                    if (newState) {
                      window.dispatchEvent(new CustomEvent('quick-actions-opened'));
                    }
                    return newState;
                  });
                }}>
                <IoMdSettings className="icon" size={20} />
                Quick Actions
                <MdKeyboardArrowDown className="icon-arrow" />
              </Button>
              {quickActive && (
                <ul className="quick-list">

                  <li>
                    <span onClick={() => handleQuickAction()}>
                      Enrich Emails
                    </span>
                  </li>
                  <li>
                    <span onClick={() => handleUpdateFilters()}>
                      Update Filters
                    </span>
                  </li>
                  <li className={!hasFilters ? 'disabled' : ''}>
                    {!hasFilters ? (
                      <div className="disabled-tooltip-wrapper">
                        <span>Resync</span>
                        <div className="disabled-tooltip">
                          This list has no filters. Please update filters to enable resync.
                        </div>
                      </div>
                    ) : (
                      <span onClick={() => handleResync()}>
                        Resync
                      </span>
                    )}
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>

      </Form>

      <div className="table-area single-list-leads-table">
        <div
          ref={filtersSidebarRef}
          className={openFilters ? 'filters-holder active' : 'filters-holder'}>
          <Filters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            campaigns={campaigns}
            tags={tags}
            lists={lists}
            hideListFilter
            filters={[
              'Lead Status',
              'Contacted Status',
              'Lead Source',
              'Campaign',
              'Tags',
              'Date Created',
              'Company Name',
              'Job Title',
              'Email',
              'LinkedIn Profile',
              'Language',
              'Location',
              'Industry',
              'Headline',
              'Open to Work',
              'Is Hiring',
            ]}
          />
        </div>
        <TableLayout
          noStyle
          onChangeFilters={filters => {
            setSearchQuery(_ => ({
              ..._,

              ...filters,
            }));
          }}
          currentPage={searchQuery?.page}
          pageSize={searchQuery?.itemsPerPage}
          totalCount={totalCount}>
          <Table rowsData={product_rows} columnNames={columnNames} noPadding />
        </TableLayout>
      </div>

      {/* Add to Campaign Modal */}
      <CenterModal
        title="Add to Campaign"
        open={addToCampaignModal.open}
        setOpen={isOpen => {
          setAddToCampaignModal({ open: isOpen, leadId: null });
          setSelectedCampaign(null);
        }}
        width="600">
        <div style={{ padding: '20px' }}>
          <AddToModalContent
            label="Select Campaign"
            placeholder="Select a campaign"
            options={campaigns}
            selectedValue={selectedCampaign}
            setSelectedValue={handleCampaignSelection}
            loading={addToCampaignLoading}
            onCancel={() => {
              setAddToCampaignModal({ open: false, leadId: null });
              setSelectedCampaign(null);
            }}
            onSubmit={handleAddLeadToCampaign}
            showPreview={Array.isArray(addToCampaignModal.leadId) && addToCampaignModal.leadId.length > 1}
            previewLoading={previewLoading}
            previewData={previewData}
          />
        </div>
      </CenterModal>

      {/* Update Filters Modal */}
      <CenterModal
        title="Update List Filters"
        open={openUpdateFiltersModal}
        setOpen={setOpenUpdateFiltersModal}
        noHead
        padding="0"
        width="975">
        <CreateListModal
          onClose={() => {
            setOpenUpdateFiltersModal(false);
            setSublistDetails(null);
          }}
          onSuccess={() => {
            setOpenUpdateFiltersModal(false);
            setSublistDetails(null);
            handleRefreshSublistLeads();
            refetch();
          }}
          isUpdateMode={true}
          existingListData={sublistDetails}
        />
      </CenterModal>

      {/* Resync Modal */}
      <CenterModal
        title="Resync Confirmation"
        open={resyncModal.open}
        setOpen={isOpen => {
          if (!isOpen) handleCloseResyncModal();
        }}
        width="500">
        <div style={{ padding: '20px' }}>
          <ResyncModalContent
            leadsAdded={resyncModal.leadsAdded}
            onClose={handleAddResyncLeadsToCampaign}
            onCancel={handleCancelResync}
          />
        </div>
      </CenterModal>

      {/* Resync Loading Modal */}
      <CenterModal open={resyncLoading} setOpen={setResyncLoading} width="600" noHead>
        <div className="leads-modal" style={{ textAlign: 'center', padding: '20px' }}>
          <strong className="heading" style={{ display: 'block', fontSize: '18px', marginBottom: '10px' }}>Processing your resync...</strong>
          <p style={{ marginBottom: '20px' }}>List has many leads, so this may take some time ⏳</p>
          <Loaders dotsLoader dotsBottom />
        </div>
      </CenterModal>
    </StyledSingleLeads>
  );
};

export default SingleListLeadsDetails;
